/**
 * EntityDetailDrawer — slide-over panel that summarises everything we
 * know about a single entity (IP, ASN, JA4, etc.) inside the active
 * filter window. State is keyed by `?entity=type:value` so deep-links
 * survive a refresh and a Slack paste.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { api } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useFilters } from "@/hooks/useFilters";
import type { EntityTypeV1 } from "@/types/v1";
import { Button, Card, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { fmtNumber } from "@/lib/format";
import { formatTime } from "@/lib/time";
import { countryName } from "@/config/geo";
import { endpointAreaGroups, endpointAreaLabel } from "@/config/filters";
import { RiskBandBadge, ActionBadge } from "@/components/badges";

interface Props {
  entity: { type: EntityTypeV1; value: string } | null;
  onClose: () => void;
  onPivot: (type: EntityTypeV1, value: string) => void;
}

export function EntityDetailDrawer({ entity, onClose, onPivot }: Props) {
  const { filters, patchFilters } = useFilters();
  const { tick } = useAutoRefresh();

  const entityType = entity?.type;
  const entityValue = entity?.value;
  const q = useQuery({
    enabled: !!entity,
    queryKey: ["entity-detail", entityType, entityValue, filters, tick],
    queryFn: ({ signal }) => api.entityDetail(filters, entityType as never, entityValue as string, signal),
  });

  // Esc to close
  useEffect(() => {
    if (!entity) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entity, onClose]);

  if (!entity) return null;

  const detail = q.data?.data;
  const activeEntity = entity;

  function applyAsFilter() {
    const map: Partial<Record<EntityTypeV1, string>> = {
      ip: "ip", asn: "asn", ja4: "ja4", ja3: "ja3", email: "email",
      business: "businessId", session: "sessionId", country: "country",
      managedList: "managedList", detectionId: "detectionId",
      userAgent: "userAgentContains", endpointGroup: "endpointGroups",
    };
    const key = map[activeEntity.type];
    if (!key) return;
    const patch = key === "endpointGroups"
      ? { endpointGroups: endpointAreaGroups(activeEntity.value) }
      : { [key]: activeEntity.value };
    patchFilters(patch as never);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
      <button
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 bg-bg/55 backdrop-blur-sm"
      />
      <aside className="flex h-full w-full max-w-[640px] flex-col overflow-y-auto border-l border-border bg-surface-1 shadow-2xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-surface-1 px-5 py-4">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wider text-fg-subtle">{entity.type}</div>
            <div className="mt-0.5 truncate font-mono text-sm text-fg" title={entity.value}>{entity.value}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="primary" onClick={applyAsFilter}>Filter Investigations</Button>
            <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </header>

        <div className="flex-1 space-y-4 px-5 py-4">
          {q.isLoading && <Skeleton className="h-64" />}
          {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
          {detail && detail.totalEvents === 0 && <EmptyState title="No events for this entity in the active window." />}

          {detail && detail.totalEvents > 0 && (
            <>
              <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Events" value={fmtNumber(detail.totalEvents, { compact: true })} />
                <Stat label="High risk" value={fmtNumber(detail.highRisk, { compact: true })} tone="high" />
                <Stat label="Suspicious" value={fmtNumber(detail.suspicious, { compact: true })} tone="suspicious" />
                <Stat label="p95 score" value={String(Math.round(detail.p95Score))} />
              </section>

              <Card padded={false} className="p-4">
                <div className="grid grid-cols-2 gap-3 text-xs text-fg-muted">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-fg-subtle">First seen</div>
                    <div className="mt-0.5 font-mono text-fg">{detail.firstSeenIso ? formatTime(detail.firstSeenIso) : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-fg-subtle">Last seen</div>
                    <div className="mt-0.5 font-mono text-fg">{detail.lastSeenIso ? formatTime(detail.lastSeenIso) : "—"}</div>
                  </div>
                </div>
              </Card>

              <TopList title="Top countries" rows={detail.topCountries.map((c) => ({
                key: c.country, label: countryName(c.country), count: c.count,
                onClick: () => onPivot("country", c.country),
              }))} />
              <TopList title="Top ASNs" rows={detail.topAsns.map((a) => ({
                key: String(a.asn), label: `AS${a.asn}`, count: a.count,
                onClick: () => onPivot("asn", String(a.asn)),
              }))} />
              <TopList title="Top JA4 fingerprints" rows={detail.topJa4s.map((j) => ({
                key: j.ja4, label: j.ja4, count: j.count, mono: true,
                onClick: () => onPivot("ja4", j.ja4),
              }))} />
              <TopList title="Top areas" rows={combineTopAreas(detail.topEndpointGroups).map((g) => ({
                key: g.group, label: g.label, count: g.count,
                onClick: () => onPivot("endpointGroup", g.group),
              }))} />

              {detail.dailySeries && detail.dailySeries.length > 1 && (
                <DailySparkline series={detail.dailySeries} />
              )}

              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">Recent events</h3>
                <div className="space-y-1">
                  {detail.recentEvents.slice(0, 12).map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-2 rounded border border-border bg-surface-2 px-2 py-1 text-xs">
                      <span className="font-mono text-fg-muted">{formatTime(e.ts)}</span>
                      <span className="font-mono text-fg">{e.score}</span>
                      <RiskBandBadge band={e.band} />
                      <span className="text-fg-muted">{endpointAreaLabel(e.endpointGroup)}</span>
                      <ActionBadge action={e.action as never} />
                      <MonoValue value={e.ip} onClick={() => onPivot("ip", e.ip)} />
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "high" | "suspicious" }) {
  return (
    <div className={clsx(
      "rounded-md border bg-surface-2 px-3 py-2",
      tone === "high" ? "border-sev-high/40" : tone === "suspicious" ? "border-sev-suspicious/40" : "border-border",
    )}>
      <div className="text-[11px] uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="mt-0.5 font-mono text-lg text-fg">{value}</div>
    </div>
  );
}

function TopList({ title, rows }: {
  title: string;
  rows: { key: string; label: string; count: number; mono?: boolean; onClick?: () => void }[];
}) {
  if (!rows.length) return null;
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{title}</h3>
      <div className="space-y-0.5">
        {rows.map((r) => (
          <button
            key={r.key}
            onClick={r.onClick}
            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-surface-2"
          >
            <span className={clsx("flex-1 truncate", r.mono && "font-mono")} title={r.label}>{r.label}</span>
            <span className="font-mono text-fg-muted">{fmtNumber(r.count, { compact: true })}</span>
            <span className="h-1.5 w-20 rounded-full bg-surface-3">
              <span className="block h-full rounded-full bg-accent/50" style={{ width: `${max ? (r.count / max) * 100 : 0}%` }} />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function combineTopAreas(groups: { group: string; count: number }[]) {
  const byLabel = new Map<string, { group: string; label: string; count: number }>();
  for (const group of groups) {
    const label = endpointAreaLabel(group.group);
    const existing = byLabel.get(label);
    if (existing) {
      existing.count += group.count;
    } else {
      byLabel.set(label, { group: group.group, label, count: group.count });
    }
  }
  return [...byLabel.values()].sort((a, b) => b.count - a.count);
}

/** Encode/decode the `?entity=type:value` URL param. */
export function parseEntityParam(s: string | undefined): { type: EntityTypeV1; value: string } | null {
  if (!s) return null;
  const idx = s.indexOf(":");
  if (idx < 1) return null;
  const type = s.slice(0, idx) as EntityTypeV1;
  const value = s.slice(idx + 1);
  if (!value) return null;
  return { type, value };
}

export function encodeEntityParam(type: EntityTypeV1, value: string): string {
  return `${type}:${value}`;
}

/**
 * DailySparkline — last-7-day total + high-risk bars. A day whose
 * high-risk count is more than 2σ above the trailing 6-day mean is
 * outlined in red so spikes pop without needing a real anomaly model.
 */
function DailySparkline({ series }: { series: { day: string; total: number; highRisk: number }[] }) {
  const last = series.slice(-7);
  const max = Math.max(1, ...last.map((d) => d.total));
  const trailing = last.slice(0, -1).map((d) => d.highRisk);
  const mean = trailing.length ? trailing.reduce((a, b) => a + b, 0) / trailing.length : 0;
  const variance = trailing.length ? trailing.reduce((a, b) => a + (b - mean) ** 2, 0) / trailing.length : 0;
  const stddev = Math.sqrt(variance);
  const todayHigh = last[last.length - 1]?.highRisk ?? 0;
  const spikeThreshold = mean + 2 * stddev;

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-subtle">Last 7 days</h3>
      <div className="flex items-end gap-1.5">
        {last.map((d, i) => {
          const isToday = i === last.length - 1;
          const isSpike = d.highRisk > spikeThreshold && stddev > 0;
          const totalH = (d.total / max) * 56;
          const highH  = (d.highRisk / max) * 56;
          return (
            <div key={d.day} className="flex flex-1 flex-col items-center gap-1" title={`${d.day.slice(0, 10)} — ${fmtNumber(d.total)} events, ${fmtNumber(d.highRisk)} high-risk`}>
              <div
                className={clsx(
                  "relative w-full rounded-sm bg-accent/30",
                  isSpike && "outline outline-1 outline-sev-high",
                )}
                style={{ height: `${totalH}px`, minHeight: "2px" }}
              >
                <div className="absolute bottom-0 left-0 right-0 rounded-sm bg-sev-high" style={{ height: `${highH}px` }} />
              </div>
              <span className={clsx("font-mono text-[10px]", isToday ? "text-fg" : "text-fg-subtle")}>
                {d.day.slice(5, 10)}
              </span>
            </div>
          );
        })}
      </div>
      {todayHigh > spikeThreshold && stddev > 0 && (
        <p className="mt-1 text-[11px] text-sev-high">
          Today's high-risk count ({fmtNumber(todayHigh)}) is more than 2σ above the trailing 6-day mean ({mean.toFixed(1)}).
        </p>
      )}
    </section>
  );
}
